import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useCart } from '../../contexts/CartContext';

const CartDrawer = () => {
  const {
    cartItems,
    selectedCaterer,
    isCartOpen,
    closeCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemsCount,
    getTotalWithFees
  } = useCart();

  if (!isCartOpen) return null;

  const formatPrice = (price) => {
    return `R${price.toFixed(2)}`;
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={closeCart}
      />
      
      {/* Drawer - responsive: full width on small screens, max 28rem on larger; safe area for notched devices */}
      <div 
        className="fixed right-0 top-0 h-full w-full max-w-[100%] sm:max-w-[28rem] md:max-w-md bg-card shadow-2xl z-50 flex flex-col animate-in slide-in-from-right-5 duration-200 overflow-hidden"
        style={{ paddingRight: 'env(safe-area-inset-right, 0)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-border min-w-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Icon name="ShoppingCart" size={22} className="text-primary flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-foreground truncate">Your Cart</h2>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {getCartItemsCount()} {getCartItemsCount() === 1 ? 'item' : 'items'}
              </p>
            </div>
          </div>
          <button
            onClick={closeCart}
            className="p-2 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
            aria-label="Close cart"
          >
            <Icon name="X" size={22} className="text-muted-foreground" />
          </button>
        </div>

        {/* Caterer Info */}
        {selectedCaterer && cartItems.length > 0 && (
          <div className="px-4 py-3 bg-muted/50 border-b border-border">
            <p className="text-sm text-muted-foreground">Ordering from</p>
            <p className="font-semibold text-foreground">{selectedCaterer.name}</p>
          </div>
        )}

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                <Icon name="ShoppingCart" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Your cart is empty</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Browse caterer menus and add items to get started
              </p>
              <Button variant="default" onClick={closeCart}>
                <Icon name="Search" size={18} />
                Browse Caterers
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => {
                const price = Number.isFinite(item.pricePerPerson) ? item.pricePerPerson : 0;
                const itemTotal = price * item.quantity;
                
                return (
                  <div 
                    key={item.id}
                    className="bg-background rounded-xl border border-border p-3 sm:p-4 overflow-hidden"
                  >
                    <div className="flex gap-3 sm:gap-4">
                      {item.image && (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0">
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-foreground line-clamp-1">{item.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {Number.isFinite(item.pricePerPerson)
                            ? (item.servingsPerUnit != null && item.servingsPerUnit > 1)
                              ? item.servingsPerUnit >= 100
                                ? `${formatPrice(item.pricePerPerson)} (serves 100+)`
                                : `${formatPrice(item.pricePerPerson)} (serves ${item.servingsPerUnit})`
                              : `${formatPrice(item.pricePerPerson)}/person`
                            : 'Contact for pricing'}
                        </p>
                      </div>
                      <button
                      onClick={() => removeFromCart(item)}
                        className="p-1 text-muted-foreground hover:text-error transition-colors"
                        aria-label="Remove item"
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    </div>
                    
                    <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-border">
                      <div className="flex items-center gap-2">
                        <span className="text-xs sm:text-sm text-muted-foreground">Guests:</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateQuantity(item, item.quantity - 1)}
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted transition-colors flex-shrink-0"
                            disabled={item.quantity <= 1}
                          >
                            <Icon name="Minus" size={14} />
                          </button>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item, parseInt(e.target.value) || 1)}
                            className="w-12 sm:w-14 h-7 sm:h-8 text-center border border-border rounded-lg bg-background text-foreground text-sm"
                            min={1}
                          />
                          <button
                            onClick={() => updateQuantity(item, item.quantity + 1)}
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted transition-colors flex-shrink-0"
                          >
                            <Icon name="Plus" size={14} />
                          </button>
                        </div>
                      </div>
                      <span className="font-semibold text-foreground text-sm sm:text-base">{formatPrice(itemTotal)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-border p-3 sm:p-4 space-y-3 sm:space-y-4 overflow-hidden flex-shrink-0">
            <div className="space-y-2 min-w-0">
              <div className="flex justify-between text-xs sm:text-sm gap-2">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground truncate">{formatPrice(getCartTotal())}</span>
              </div>
              <div className="flex justify-between text-xs sm:text-sm gap-2">
                <span className="text-muted-foreground">Service fee (5%)</span>
                <span className="text-foreground truncate">{formatPrice(getCartTotal() * 0.05)}</span>
              </div>
              <div className="flex justify-between font-semibold text-base sm:text-lg pt-2 border-t border-border gap-2">
                <span className="text-foreground">Total</span>
                <span className="text-primary truncate">{formatPrice(getTotalWithFees())}</span>
              </div>
            </div>
            
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={clearCart}
                className="flex-shrink-0 w-full sm:w-auto"
              >
                <Icon name="Trash2" size={18} />
              </Button>
              <Link to="/checkout" className="flex-1 min-w-0" onClick={closeCart}>
                <Button variant="default" fullWidth>
                  <Icon name="CreditCard" size={18} />
                  Checkout
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
